// Angular imports
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';

// CC-Common imports
import { Utils } from '@therealmanage/ciranet-ui-common';

// Component imports
import { BaseComponent } from '../../_base/rp-base-component';
import { SideNavigationMenuComponent } from '../side-navigation-menu/side-navigation-menu.component';

// Service imports
import { AuthService } from '../../_services/auth.service';
import { UserService } from '../../_services/data-services/user.service';
import { CiraHelpService } from '../../_services/shared-services/cira-help.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { ScreenService } from '../../_services/shared-services/screen.service';

// Model imports
import { ActionItemConfigResultDto } from '../../_models/action-item-config-result-dto';
import { BaseResidentParameterDto } from '../../_models/base-resident-parameter-dto';
import { SideNavMenuInfo } from '../../_models/side-nav-menu-info';
import { UserPropertyResultDto } from '../../_models/user-property-result-dto';

// Constant imports
import { SideNavigationMenus } from '../../_constants/side-navigation-menus';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-side-nav-outer-toolbar',
  templateUrl: './side-nav-outer-toolbar.component.html',
  styleUrls: ['./side-nav-outer-toolbar.component.scss'],
  standalone: false
})
export class SideNavOuterToolbarComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  @Input() title: string;

  @ViewChild('sideNavigationMenu', { static: false })
  sideNavigationMenu: SideNavigationMenuComponent;

  topMenuItems: Array<object> = [];
  bottomMenuItems: Array<object> = [];
  selectedRoute = '/home';
  selectedProperty: UserPropertyResultDto;
  menuOpened: boolean;
  temporaryMenuOpened = false;
  menuMode = 'shrink';
  menuRevealMode = 'expand';
  minMenuSize = 0;
  shaderEnabled = false;
  enableSideMenu = false;
  helpLink: string;

  constructor(
    screenService: ScreenService,
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private helperService: HelperService,
    private ciraHelpService: CiraHelpService
  ) {
    super(screenService);
  }

  ngOnInit() {
    this.enableSideMenu = this.loggedIn() == true;
    this.menuOpened = this.screenService.sizes['screen-large'];

    this.createSideMenu(true); //this is for refresh
    this.updateDrawer();
    this.updateHelpLink();

    RxjsUtils.subscribeToRouterEvents(this.router, (event: Event) => {
      if (event instanceof NavigationEnd) {
        const selectedRoute = event.urlAfterRedirects.split('?')[0];
        if (this.selectedRoute !== selectedRoute && selectedRoute.length > 0) {
          this.selectedRoute = selectedRoute;
        }
        if (
          !!this.enableSideMenu &&
          !!this.sideNavigationMenu &&
          this.selectedRoute.length > 0
        ) {
          this.sideNavigationMenu.selectedItem = this.selectedRoute;
        }
      }
    });

    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateDrawer()
    );

    // Listen for sidebar close requests from property-banner on mobile
    RxjsUtils.safeSubscribe(
      this.helperService.subjectCloseSideNav.asObservable(),
      () => {
        if (this.menuOpened) {
          this.menuOpened = false;
        }
      }
    );

    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectPropertySelectionChanged(),
      (selectedProperty) => {
        if (
          (!!this.selectedProperty &&
            !!selectedProperty &&
            (this.selectedProperty.communityId !==
              selectedProperty.communityId ||
              this.selectedProperty.customerId !==
                selectedProperty.customerId)) ||
          (!this.selectedProperty && !!selectedProperty) ||
          (!!this.selectedProperty && !selectedProperty)
        ) {
          setTimeout(() => {
            const propertyClass = 'property-container';
            const propertyElement =
              document.getElementsByClassName(propertyClass)[0];
            if (propertyElement?.parentElement) {
              const propertyContainer: any = propertyElement.parentElement;
              propertyContainer.style.height = 'auto';
            }
            // Emit current sidebar state to update banner width
            this.helperService.subjectSideNavSizeChanged.next(this.menuOpened);
          }, 100);
          this.createSideMenu(true);
          this.updateHelpLink();
        }
        this.selectedProperty = selectedProperty;
      }
    );
  }

  ngAfterViewInit(): void {
    //fix the issue that there is a big gap between side menu and page content in Safari.
    const overlayClass = 'dx-drawer-panel-content';
    if (document.getElementsByClassName(overlayClass).length > 0) {
      const item: any = document.getElementsByClassName(overlayClass)[0];
      item.style.width = this.getMenuWidth();
    }

    const className2 = 'dx-overlay-wrapper';
    if (document.getElementsByClassName(className2).length > 0) {
      const item: any = document.getElementsByClassName(className2)[0];
      item.style.width = this.getMenuWidth();
      item.style.transform = 'translate(0px,0px)';
      item.style.height = '100%';
    }
  }

  get hideMenuAfterNavigation(): string | boolean {
    return this.menuMode === 'overlap' || this.temporaryMenuOpened;
  }

  get showMenuAfterClick(): boolean {
    return !this.menuOpened;
  }

  //hamburger menu click event handler
  processMenuToggle(): void {
    this.menuOpened = !this.menuOpened;
    this.helperService.subjectSideNavSizeChanged.next(this.menuOpened);
    //! this one fixed the hamburger menu issue
    setTimeout(() => {
      const overlayClass = 'dx-overlay-content';
      const drawerPanelOverlayClass =
        'dx-drawer-panel-content dx-overlay dx-widget dx-visibility-change-handler';
      const drawerPanelClass = 'dx-drawer-panel-content';
      const propertyClass = 'property-container';
      const className3 = 'dx-overlay-wrapper';
      const isMedium = this.screenService.sizes['screen-medium'];

      if (this.screenService.isXSmallScreen() || !!isMedium) {
        //! in real iPhone device.
        if (document.getElementsByClassName(overlayClass).length > 0) {
          const overlays: any =
            document.getElementsByClassName('dx-overlay-content');

          for (const item of overlays) {
            if (!item.classList.contains('dx-popup-normal')) {
              item.style.width = this.getMenuWidth();
              item.style.transform = 'translate(0px,0px)';
            }
          }

          if (
            document.getElementsByClassName(drawerPanelOverlayClass).length > 0
          ) {
            const item2: any = document.getElementsByClassName(
              drawerPanelOverlayClass
            )[0];

            if (this.menuOpened) {
              item2.style.transform = 'translate(0px,0px)';
            } else {
              item2.style.height = '100%';
            }
          }

          // Fixed the hamburger menu issue where only half of it was visible after new themes were added.
          if (document.getElementsByClassName(className3).length > 0) {
            const item3: any = document.getElementsByClassName(className3)[0];

            if (this.menuOpened) {
              item3.style.transform = 'translate(0px, 0px)';
              item3.style.height = '100%';
            } else {
              item3.style.height = '100%';
            }
          }
        }
      } else {
        // > xsmall devices:
        if (document.getElementsByClassName(overlayClass).length > 0) {
          const item: any = document.getElementsByClassName(overlayClass)[0];

          item.style.width = this.getMenuWidth();
          item.style.transform = 'translate(0px,0px)';
        }
        if (
          document.getElementsByClassName(drawerPanelOverlayClass).length > 0
        ) {
          const item: any = document.getElementsByClassName(
            drawerPanelOverlayClass
          )[0];

          item.style.width = this.getMenuWidth();
          item.style.transform = 'translate(0px,0px)';
        }
        if (
          document.getElementsByClassName(drawerPanelClass).length > 0 &&
          this.menuOpened
        ) {
          const item: any =
            document.getElementsByClassName(drawerPanelClass)[0];

          item.style.width = this.getMenuWidth();
          item.style.transform = 'translate(0px,0px)';
        }

        const propertyElement =
          document.getElementsByClassName(propertyClass)[0];
        if (propertyElement?.parentElement) {
          const propertyContainer: any = propertyElement.parentElement;
          if (!this.menuOpened) {
            propertyContainer.style.height = '0px';
          } else {
            propertyContainer.style.height = 'auto';
          }
        }
      }
    }, 100);
  }

  isNeedImpersonate(): boolean {
    if (this.userService.isInternal() == true) {
      const userID = this.userService.impersonatedUserId();
      if (userID == null) {
        return true;
      }
    }
    return false;
  }

  createSideMenu(clearCache: boolean = false): void {
    this.bottomMenuItems = SideNavigationMenus.bottomMenuItems.slice();
    const selectedProperty: UserPropertyResultDto =
      this.userService.getSelectedProperty();
    if (!!selectedProperty) {
      const baseParams: BaseResidentParameterDto =
        new BaseResidentParameterDto();
      baseParams.communityId = selectedProperty.communityId;
      baseParams.customerId = selectedProperty.customerId;
      baseParams.clearCache = clearCache;
      RxjsUtils.safeSubscribe(
        this.userService.getUserActionItems(baseParams),
        (actions: ActionItemConfigResultDto[]) => {
          const filterFn = (menuItem: SideNavMenuInfo) => {
            if (!menuItem.dynamic) {
              return true;
            }
            return (
              actions.findIndex((actionMenu: ActionItemConfigResultDto) => {
                return actionMenu.item === menuItem.name && !!actionMenu.enable;
              }) !== -1
            );
          };
          this.topMenuItems = this.getFilteredMenuItems(filterFn);
        }
      );
    } else {
      const filterFn = (menuItem: SideNavMenuInfo) => {
        return menuItem.dynamic === false;
      };
      this.topMenuItems = this.getFilteredMenuItems(filterFn);
    }
  }

  updateDrawer(): void {
    setTimeout(() => {
      const isLarge = this.screenService.sizes['screen-large'];
      const isMedium = this.screenService.sizes['screen-medium'];
      const useShrinkMode = isLarge || isMedium;

      this.menuMode = useShrinkMode ? 'shrink' : 'overlap';
      this.menuRevealMode = this.isXtraSmall ? 'slide' : 'expand';
      this.minMenuSize = this.isXtraSmall ? 0 : 60;
      this.shaderEnabled = !useShrinkMode;

      // Collapse sidebar when screen is not large (e.g., dev tools opened)
      if (!isLarge && this.menuOpened) {
        this.menuOpened = false;
        this.helperService.subjectSideNavSizeChanged.next(this.menuOpened);
      }
    }, 200);
  }

  navigationChanged(event): void {
    const path = event.itemData.path;
    const pointerEvent = event.event;

    if (this.selectedRoute === path) {
      if (Utils.notNullAndDefined(pointerEvent)) {
        pointerEvent.preventDefault();
      }
      return; // just exit, no need to return a value
    } else if (path && this.selectedRoute !== path) {
      this.router.navigate([path]);

      if (this.hideMenuAfterNavigation) {
        this.temporaryMenuOpened = false;
        this.menuOpened = false;
        // Notify property-banner of sidebar state change
        this.helperService.subjectSideNavSizeChanged.next(this.menuOpened);

        if (Utils.notNullAndDefined(pointerEvent)) {
          pointerEvent.stopPropagation();
        }
      }
    } else {
      const value = event.itemData.value;
      if (value === 'help') {
        this.ciraHelpService.showHelp(this.helpLink);
      }

      if (Utils.notNullAndDefined(pointerEvent)) {
        pointerEvent.preventDefault();
      }
    }
  }

  navigationClick(): void {
    //fix ipad issue that the menu just shows half
    setTimeout(() => {
      if (this.showMenuAfterClick) {
        this.temporaryMenuOpened = true;
        this.menuOpened = true;
        // Notify property-banner of sidebar state change
        this.helperService.subjectSideNavSizeChanged.next(this.menuOpened);
        //! this one fixed the hamburger menu issue in real iPhone device.
        const overlayClass = 'dx-drawer-panel-content';
        if (document.getElementsByClassName(overlayClass).length > 0) {
          const item: any = document.getElementsByClassName(overlayClass)[0];

          item.style.width = this.getMenuWidth();
          item.style.transform = 'translate(0px,0px)';
        }

        const propertyClass = 'property-container';
        const propertyElement =
          document.getElementsByClassName(propertyClass)[0];
        if (propertyElement?.parentElement) {
          const propertyContainer: any = propertyElement.parentElement;
          if (!this.menuOpened) {
            propertyContainer.style.height = '0px';
          } else {
            propertyContainer.style.height = 'auto';
          }
        }

        //! this one fixed the hamburger menu issue in real iPhone device.
        const className1 = 'dx-overlay-content';
        if (document.getElementsByClassName(className1).length > 0) {
          const item: any = document.getElementsByClassName(className1)[0];
          item.style.width = this.getMenuWidth();
          item.style.transform = 'translate(0px,0px)';
        }
      }
    }, 500);
  }

  loggedIn(): boolean {
    return this.authService.isLoggedIn;
  }

  onSidebarStateChanged(opened: boolean): void {
    // Notify property-banner of sidebar state change (handles outside click close)
    this.helperService.subjectSideNavSizeChanged.next(opened);
  }

  getMenuWidth(): string {
    //fix the issue where controls on the left hand side can't be touch because the side menu overlaps.
    const isLarge = this.screenService.sizes['screen-large'];
    const isMedium = this.screenService.sizes['screen-medium'];
    const useShrinkMode = isLarge || isMedium;
    const mode =
      useShrinkMode || this.menuOpened == true ? 'shrink' : 'overlap'; //https://js.devexpress.com/Demos/WidgetsGallery/Demo/Drawer/LeftOrRightPosition/Angular/Light/
    return mode == 'shrink' ? '300px' : '60px';
  }

  private getFilteredMenuItems(
    filterFn: (menuItem: SideNavMenuInfo) => NonNullable<any>
  ): SideNavMenuInfo[] {
    const filteredHomeMenuItems =
      SideNavigationMenus.homeMenuItems.filter(filterFn);
    const filteredPaymentMenuItems =
      SideNavigationMenus.paymentMenuItems.slice();
    filteredPaymentMenuItems[0].items =
      SideNavigationMenus.paymentSubMenuItems.filter(filterFn);
    const filteredPropertyMenuItems =
      SideNavigationMenus.propertyMenuItems.slice();
    filteredPropertyMenuItems[0].items =
      SideNavigationMenus.propertySubMenuItems.filter(filterFn);
    const filteredCommunityMenuItems =
      SideNavigationMenus.communityMenuItems.slice();
    filteredCommunityMenuItems[0].items =
      SideNavigationMenus.communitySubMenuItems.filter(filterFn);
    const filteredTopMenuItems = [].concat(
      filteredHomeMenuItems,
      filteredPaymentMenuItems,
      filteredPropertyMenuItems,
      filteredCommunityMenuItems
    );
    return filteredTopMenuItems;
  }

  private updateHelpLink(): void {
    this.selectedProperty = this.userService.getSelectedProperty();
    if (!!this.selectedProperty && !!this.selectedProperty.communityId) {
      this.helpLink = this.ciraHelpService.getSideNavHelpLink(
        this.selectedProperty.communityId
      );
    } else {
      this.helpLink = this.ciraHelpService.getSideNavHelpLink(null);
    }
  }
}
